import { toISODateTime } from 'utils/dateUtils';

const serializeDateBoundaryValue = (value: Date) =>
  toISODateTime(value, { zone: 'utc' });

/**
 * stripNested
 * Creates a shallow logging-friendly snapshot of an object by:
 *  - Preserving only primitive values (string | number | boolean | null)
 *  - Converting Date objects to ISO strings through the shared date utilities
 *  - Replacing nested objects with their ID (first matching key from idKeys)
 *  - Replacing arrays with an array of IDs (or primitive entries). For an object element
 *    in an array, only the ID is kept. If no ID can be resolved the entry becomes null.
 *  - Omits keys whose value is an object and no ID key is found.
 *
 * This is intended for safe-ish telemetry/logging (still review for PHI before use).
 */
export const stripNested = (
  input: any,
  options?: {
    idKeys?: string[];
    keepNulls?: boolean; // if false, keys resolving to undefined are dropped
  }
): Record<string, any> => {
  const idKeys = options?.idKeys || [
    'id',
    'ID',
    'providerID',
    'patientID',
    'healthcardID',
    'tariffID',
  ];

  if (input == null || typeof input !== 'object' || Array.isArray(input)) {
    throw new Error('stripNested expects a plain object as the root input');
  }

  const out: Record<string, any> = {};
  const findId = (obj: any) => {
    if (!obj || typeof obj !== 'object') return undefined;
    for (const k of idKeys) {
      if (Object.prototype.hasOwnProperty.call(obj, k)) return obj[k];
    }
    return undefined;
  };

  Object.entries(input).forEach(([key, value]) => {
    if (key === 'internalComments') return;
    if (
      value === null ||
      ['string', 'number', 'boolean'].includes(typeof value)
    ) {
      out[key] = value;
      return;
    }
    if (value instanceof Date) {
      out[key] = serializeDateBoundaryValue(value);
      return;
    }
    if (Array.isArray(value)) {
      out[key] = value.map((entry) => {
        if (
          entry === null ||
          ['string', 'number', 'boolean'].includes(typeof entry)
        ) {
          return entry;
        }
        if (entry instanceof Date) return serializeDateBoundaryValue(entry);
        if (typeof entry === 'object') {
          const id = findId(entry);
          return id !== undefined ? id : null;
        }
        return null;
      });
      return;
    }
    if (typeof value === 'object') {
      const id = findId(value);
      if (id !== undefined) {
        // Keep same key name; if it already ends with ID we keep it; else we could also alias but keep simple.
        out[key] = id;
      } else if (options?.keepNulls) {
        out[key] = null;
      }
      return;
    }
    // Fallback: ignore unsupported types (functions, symbols, etc.)
  });

  return out;
};

type NavigateToPathOptions = {
  path: string;
};

export const navigateToPath = ({ path }: NavigateToPathOptions): void => {
  if (typeof window === 'undefined') return;

  window.open(path, '_blank', 'noopener,noreferrer');
};
